/**
 * F17 T17.1.5 — modal "Nowy proces" wywoływany z listy procesów.
 *
 * Pola: nazwa (required, max 200) + opis (optional, max 2000) + targetModuleCode
 * (select z whitelisty `SUPPORTED_PROCESS_TARGET_MODULES` z `@saas/shared`).
 *
 * **UWAGA — kontrakt z backendem:** `targetModuleCode` MUSI być qualified
 * (np. `'crm_core.contractors'`), a NIE bare slug (`'contractors'`). Engine
 * (`TARGET_MODULE_FETCH_MAP` w `apps/api/lib/processes/engine.ts`) dispatch'uje
 * po qualified code. `system_modules.slug` w DB jest bare ('contractors')
 * i NIE pasuje — dlatego MVP używa hardcoded whitelisty zamiast fetch
 * `/api/system-modules`. Rozszerzenie listy = dodać tutaj + w engine map.
 *
 * Po POST sukces: callback `onCreated(processId)` — caller robi navigate
 * `/processes/builder/${id}` żeby admin od razu mógł rysować graf.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_PROCESS_TARGET_MODULES } from '@saas/shared';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { createProcess } from '@/lib/processes/api';

export interface CreateProcessModalProps {
  open: boolean;
  onClose: () => void;
  /** Wywoływane po pomyślnym utworzeniu — caller robi navigate do buildera. */
  onCreated: (processId: string) => void;
}

export function CreateProcessModal({ open, onClose, onCreated }: CreateProcessModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Default → pierwszy z whitelisty (w MVP jedyny moduł `crm_core.contractors`).
  const [targetModuleCode, setTargetModuleCode] = useState<string>(
    SUPPORTED_PROCESS_TARGET_MODULES[0],
  );

  // Reset formularza przy zamknięciu modalu.
  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setTargetModuleCode(SUPPORTED_PROCESS_TARGET_MODULES[0]);
    }
  }, [open]);

  // T17.1.9 — custom moduły tenanta jako cele procesu (kod `custom.<slug>`),
  // obok modułów systemowych z whitelisty. Statyczna lista nie pomieści
  // dynamicznych slugów, więc dociągamy je z GET /api/modules.
  const customModulesQuery = useQuery({
    queryKey: ['modules', 'for-process-target'],
    queryFn: async () => {
      const res = await api.get<{
        modules: Array<{ id: string; slug: string; name: string }>;
      }>('/modules');
      return res.data.modules;
    },
    enabled: open,
  });
  // Błąd pobrania = brak opcji custom (moduły systemowe nadal wybieralne). Świadomy
  // nieblokujący fallback — to tylko wzbogacenie dropdownu, nie operacja krytyczna,
  // więc bez toast/alert.
  const customModules = customModulesQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: async () => {
      return createProcess({
        name: name.trim(),
        description: description.trim() || null,
        targetModuleCode,
      });
    },
    onSuccess: (process) => {
      toast.show({
        variant: 'success',
        title: t('tenant.processes.list.createSuccess'),
      });
      onCreated(process.id);
      onClose();
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.processes.list.createError'),
      });
    },
  });

  const nameInvalid = name.trim().length === 0 || name.trim().length > 200;
  const canSubmit = !nameInvalid && targetModuleCode !== '' && !createMutation.isPending;

  function handleSubmit() {
    if (!canSubmit) return;
    createMutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.processes.list.createModal.title')}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="rounded-lg border border-border-secondary px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
            data-testid="create-process-cancel-btn"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="create-process-submit-btn"
          >
            {createMutation.isPending
              ? t('common.saving')
              : t('tenant.processes.list.createModal.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="create-process-name"
            className="mb-1 block text-sm font-medium text-text-primary"
          >
            {t('tenant.processes.list.createModal.nameLabel')}
            <span className="ml-1 text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <Input
            id="create-process-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder={t('tenant.processes.list.createModal.namePlaceholder')}
            data-testid="create-process-name-input"
          />
        </div>

        <div>
          <label
            htmlFor="create-process-module"
            className="mb-1 block text-sm font-medium text-text-primary"
          >
            {t('tenant.processes.list.createModal.moduleLabel')}
            <span className="ml-1 text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <Dropdown
            id="create-process-module"
            value={targetModuleCode}
            onChange={(value) => setTargetModuleCode(value)}
            options={[
              // i18next keySeparator='.' — dot w qualified code kolidowałaby
              // ze ścieżką, więc używamy '_' w kluczu i replace przy lookup.
              ...SUPPORTED_PROCESS_TARGET_MODULES.map((code) => ({
                value: code,
                label: t(`tenant.processes.list.createModal.modules.${code.replace(/\./g, '_')}`),
              })),
              // T17.1.9 — custom moduły tenanta (kod `custom.<slug>`, label = nazwa modułu).
              ...customModules.map((m) => ({ value: `custom.${m.slug}`, label: m.name })),
            ]}
            data-testid="create-process-module-select"
          />
        </div>

        <div>
          <label
            htmlFor="create-process-description"
            className="mb-1 block text-sm font-medium text-text-primary"
          >
            {t('tenant.processes.list.createModal.descriptionLabel')}
          </label>
          <textarea
            id="create-process-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder={t('tenant.processes.list.createModal.descriptionPlaceholder')}
            className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm focus:border-accent-500 focus:outline-none"
            data-testid="create-process-description-input"
          />
        </div>
      </div>
    </Modal>
  );
}
